import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { getTags } from '../services/tagApi';
import { Tag } from '../types/tag';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import TagModal from '../utils/TagModal';
import AddTagModal from '../components/tag/AddTagModal';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Tag[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Tag | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isTagModalOpen, setIsTagModalOpen] = useState<boolean>(false);
    const [isAddTagModalOpen, setIsAddTagModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getTags().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const toggleTagModal = () => {
        setIsTagModalOpen(!isTagModalOpen);
    };

    const toggleAddTagModal = () => {
        setIsAddTagModalOpen(!isAddTagModalOpen);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorFn: (row: any) => {
                return row.name ? row.name : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Site',
            accessorKey: 'domain',
            cell: (cell: any) => {
                const cellValue = cell.getValue();
                return cellValue ? (
                    <>
                        {cell.getValue()}{' '}
                        <a href={'https://' + cellValue} target="_blank" rel="noreferrer">
                            <i className="ri-external-link-line"></i>
                        </a>
                    </>
                ) : (
                    '-'
                );
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Habilitado',
            accessorKey: 'enabled',
            cell: (cell: any) => {
                const isEnabled = cell.row.original.enabled;
                const statusStr = isEnabled ? 'Habilitado' : 'Desabilitado';
                const bgColor = isEnabled ? 'success' : 'danger';
                return <span className={`badge bg-${bgColor}`}>{statusStr}</span>;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Criado em',
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Atualizado em',
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return row.updatedAt ? date.toLocaleDateString('pt-BR', dateFormatOptions) : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Sync GAM',
            cell: (cell: any) => {
                const placements = cell.row.original.placements || [];
                const total = placements.length;
                const synced = placements.filter((p: any) => p.isSynced).length;
                const allSynced = total > 0 && synced === total;
                const noneSynced = synced === 0;
                const status = allSynced ? 'success' : noneSynced ? 'danger' : 'warning';

                if (total === 0) return <span className="text-muted">-</span>;

                return (
                    <span title={`${synced}/${total} placements sincronizados`}>
                        <i className={`ri-refresh-line fs-16 text-${status}`}></i>
                        <span className={`ms-1 badge bg-${status}-subtle text-${status}`}>
                            {synced}/{total}
                        </span>
                    </span>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                if (
                    userProfile &&
                    (userProfile.role === 'ROLE_SUPER_ADMIN' ||
                        userProfile.role === 'ROLE_PUBLISHER_ADMIN' ||
                        userProfile.role === 'ROLE_AD_NETWORK_ADMIN')
                ) {
                    actionCell.push(
                        <div key="action_tag">
                            <button
                                className="btn text-primary d-inline-block"
                                id={'action_tag_' + row.id}
                                onClick={() => {
                                    setDataToEdit(row);
                                    toggleTagModal();
                                }}
                                title="TAG"
                            >
                                <i className="ri-hashtag fs-16"></i>
                            </button>
                        </div>
                    );

                    actionCell.push(
                        <div key="action_edit">
                            <button
                                className="btn text-primary d-inline-block"
                                id={'action_edit_' + row.id}
                                onClick={() => {
                                    window.location.href = `/tag/${row.id}`;
                                }}
                                title="Editar"
                            >
                                <i className="ri-pencil-fill fs-16"></i>
                            </button>
                        </div>
                    );
                }
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'TAGs | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">TAGs</h5>
                                        </div>
                                        <div className="col-sm-auto">
                                            <button
                                                type="button"
                                                className="btn btn-info add-btn"
                                                onClick={toggleAddTagModal}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i> Nova TAG
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {!loading ? (
                                        <TableContainer
                                            divClass="table-responsive"
                                            columns={(columns || [])}
                                            data={(data || [])}
                                            isGlobalFilter={true}
                                            customPageSize={30}
                                            SearchPlaceholder="Procurar..."
                                        />
                                    ) : (
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {dataToEdit && (
                <TagModal tag={dataToEdit} isOpen={isTagModalOpen} toggle={toggleTagModal} />
            )}

            <AddTagModal isOpen={isAddTagModalOpen} toggle={toggleAddTagModal} />
        </React.Fragment>
    );
};

export default withRouter(Index);
